export enum SpaceTypeKey {
	Residential = 'residential',
	Commercial = 'commercial',
	Healthcare = 'healthcare',
	Educational = 'educational',
	Retail = 'retail',
	MultiFamily = 'multi-family',
	Hospitality = 'hospitality',
}

interface SpaceType {
	name: string;
	target: number;
	defaultSTC: number;
}

interface NoiseZone {
	range: string;
	averageNoise: number;
	area: number;
	percentage: number;
}

interface ScoreResult {
	zone: string;
	exteriorNoise: number;
	interiorNoise: number;
	variance: number;
	score: number;
	color: string;
	performance: string;
	action: string;
}

export interface AnalysisResult {
	spaceType: SpaceTypeKey;
	targetNoise: number;
	stcRating: number;
	noiseReduction: number;
	exteriorNoise: number;
	interiorNoise: number;
	variance: number;
	penalty: number;
	weightedScore: number;
	overallPerformance: string;
	scoreColor: string;
	action: string;
	zones: ScoreResult[];
	recommendation: string;
}

interface ScoreLevel {
	min: number;
	max: number;
	color: string;
	performance: string;
	action: string;
}

const SPACE_TYPES: Record<SpaceTypeKey, SpaceType> = {
	[SpaceTypeKey.Residential]: {
		name: 'Residential',
		target: 35,
		defaultSTC: 35,
	},
	[SpaceTypeKey.Commercial]: {
		name: 'Commercial',
		target: 40,
		defaultSTC: 40,
	},
	[SpaceTypeKey.Healthcare]: {
		name: 'Healthcare',
		target: 35,
		defaultSTC: 45,
	},
	[SpaceTypeKey.Educational]: {
		name: 'Educational',
		target: 35,
		defaultSTC: 42,
	},
	[SpaceTypeKey.Retail]: {
		name: 'Retail',
		target: 45,
		defaultSTC: 30,
	},
	[SpaceTypeKey.MultiFamily]: {
		name: 'Multi-Family',
		target: 35,
		defaultSTC: 35,
	},
	[SpaceTypeKey.Hospitality]: {
		name: 'Hospitality',
		target: 40,
		defaultSTC: 38,
	},
};

// @note Maps STC values (20-60) to noise-reduced values by subtracting 12
const STC_NOISE_REDUCTION = Object.fromEntries(
	Array.from({ length: 41 }, (_, i) => [i + 20, i + 8])
) as Record<number, number>;

const SCORE_LEVELS: ScoreLevel[] = [
	{
		min: 10,
		max: 10,
		color: '#29b349ff',
		performance: 'Excellent',
		action: 'No treatment required',
	},
	{
		min: 8,
		max: 9,
		color: '#19642aff',
		performance: 'Good',
		action: 'Optional treatment',
	},
	{
		min: 6,
		max: 7,
		color: '#fff344ff',
		performance: 'Acceptable',
		action: 'Consider treatment',
	},
	{
		min: 4,
		max: 5,
		color: '#f0c848ff',
		performance: 'Poor',
		action: 'Recommended treatment',
	},
	{
		min: 2,
		max: 3,
		color: '#bd2f3bff',
		performance: 'Very Poor',
		action: 'Required treatment',
	},
	{
		min: 0,
		max: 1,
		color: '#6e1920ff',
		performance: 'Critical',
		action: 'Avoid placement',
	},
];

class NoiseAnalysisBioScore {
	private interpolateSTC(stc: number): number {
		const keys = Object.keys(STC_NOISE_REDUCTION)
			.map(Number)
			.sort((a, b) => a - b);
		const min = keys[0],
			max = keys[keys.length - 1];

		if (stc <= min) return STC_NOISE_REDUCTION[min];
		if (stc >= max) return STC_NOISE_REDUCTION[max];

		const lower = keys.find((k) => k <= stc)!;
		const upper = keys.find((k) => k > stc)!;
		const ratio = (stc - lower) / (upper - lower);

		return (
			STC_NOISE_REDUCTION[lower] +
			(STC_NOISE_REDUCTION[upper] - STC_NOISE_REDUCTION[lower]) * ratio
		);
	}

	private getScoreLevel(score: number): ScoreLevel {
		const roundedScore = Math.round(score);
		return (
			SCORE_LEVELS.find(({ min, max }) => roundedScore >= min && roundedScore <= max) ||
			SCORE_LEVELS[SCORE_LEVELS.length - 1]
		);
	}

	private calculateScore(interiorNoise: number, target: number): number {
		return Math.max(0, 10 - Math.ceil(Math.max(0, interiorNoise - target) / 5));
	}

	private analyzeZone(zone: NoiseZone, stcReduction: number, target: number): ScoreResult {
		const exteriorNoise = zone.averageNoise;
		const interiorNoise = exteriorNoise - stcReduction;
		const score = this.calculateScore(interiorNoise, target);
		const { color, performance, action } = this.getScoreLevel(score);

		return {
			zone: zone.range,
			exteriorNoise,
			interiorNoise,
			variance: Math.max(0, interiorNoise - target),
			score,
			color,
			performance,
			action,
		};
	}

	private getRecommendation(weightedScore: number): string {
		if (weightedScore >= 8)
			return 'No treatment required. Noise conditions meet performance targets.';
		if (weightedScore >= 6)
			return 'Minor treatment may be considered in specific zones to optimize comfort.';
		if (weightedScore >= 4)
			return 'Noise treatment recommended to enhance interior noise performance.';
		return 'Comprehensive noise improvement required to achieve acceptable performance levels.';
	}

	public analyze(spaceTypeKey: SpaceTypeKey, noiseZones: NoiseZone[]): AnalysisResult {
		const spaceType = SPACE_TYPES[spaceTypeKey];
		if (!spaceType) throw new Error(`Unknown space type: ${spaceTypeKey}`);

		const stcRating = spaceType.defaultSTC;
		const stcReduction = this.interpolateSTC(stcRating);
		const zoneScores = noiseZones.map((z) =>
			this.analyzeZone(z, stcReduction, spaceType.target)
		);

		const totalArea = noiseZones.reduce((sum, z) => sum + z.area, 0);

		// Calculate weighted averages for overall results
		const weightedExteriorNoise = zoneScores.reduce(
			(sum, res, i) => sum + res.exteriorNoise * (noiseZones[i].area / totalArea),
			0
		);
		const weightedInteriorNoise = zoneScores.reduce(
			(sum, res, i) => sum + res.interiorNoise * (noiseZones[i].area / totalArea),
			0
		);
		const weightedScore = zoneScores.reduce(
			(sum, res, i) => sum + res.score * (noiseZones[i].area / totalArea),
			0
		);

		const roundedWeightedScore = Math.round(weightedScore * 100) / 100;
		const roundedWeightedExterior = Math.round(weightedExteriorNoise * 100) / 100;
		const roundedWeightedInterior = Math.round(weightedInteriorNoise * 100) / 100;
		const variance = Math.max(0, roundedWeightedInterior - spaceType.target);
		const penalty = Math.ceil(variance / 5);

		const {
			performance: overallPerformance,
			action,
			color: scoreColor,
		} = this.getScoreLevel(roundedWeightedScore);

		return {
			spaceType: spaceTypeKey,
			targetNoise: spaceType.target,
			stcRating,
			noiseReduction: Math.round(stcReduction * 100) / 100,
			exteriorNoise: roundedWeightedExterior,
			interiorNoise: roundedWeightedInterior,
			variance,
			penalty,
			weightedScore: roundedWeightedScore,
			overallPerformance,
			scoreColor,
			action,
			zones: zoneScores,
			recommendation: this.getRecommendation(roundedWeightedScore),
		};
	}
}

export default NoiseAnalysisBioScore;
